/**
 * tree.ts — Extremely Randomized Trees (Geurts, Ernst, Wehenkel 2006), a
 * faithful TypeScript port of sklearn's ExtraTreeRegressor.
 *
 * The algorithm (one tree, regression):
 *
 *   At each node, for each of the max_features randomly-drawn features:
 *     1. Find min/max of that feature over the samples currently in the node.
 *     2. If max - min <= FEATURE_THRESHOLD (constant feature), skip it.
 *     3. Draw ONE threshold uniformly in [min, max).  ← the defining move
 *     4. Partition the samples by x[f] <= threshold  vs  x[f] > threshold.
 *     5. Reject if either child has fewer than min_samples_leaf samples.
 *     6. Score the split by variance reduction, maximise across features.
 *
 *   Split criterion (regression MSE): the proxy we MAXIMISE is
 *        sum_left²/n_left + sum_right²/n_right
 *   which is algebraically the same as minimising within-child variance
 *   (because Σy² over the parent is constant). This is sklearn's
 *   proxy_impurity_improvement.
 *
 *   Leaf value = mean of y over samples reaching it.
 *
 * Differences from Random Forest (the two axes of ExtraTrees randomness):
 *   - bootstrap=False: every tree sees the full dataset.
 *   - threshold is a single random draw in [min,max), NOT the optimal split.
 *
 * Tree layout: parallel typed arrays (feature / threshold / left / right /
 * value / leaf), as in sklearn and mljs/decision-tree-cart. Cache-friendly
 * and transferable to a Web Worker as a bag of TypedArray buffers.
 *
 * Numerical faithfulness: the load-bearing property for the reflex bandit
 * is the axis-aligned split on the 75-dim one-hot token field — a single
 * split on column 12*3+1 = 37 is literally "is there GOLD at the
 * destination cell?". This is algorithmic, not numerical: a faithful port
 * reproduces the 97%+ gold-grab without bit-exact sklearn parity.
 */

/** Features whose value range is below this are treated as constant. */
const FEATURE_THRESHOLD = 1e-7;

/** Pre-allocated working buffers per node, sized to max samples. */
interface WorkBuffers {
  // Indices for the left / right partitions.
  leftIdx: Int32Array;
  rightIdx: Int32Array;
  // Per-feature running min/max for the sample-outer pass.
  featMin: Float64Array;
  featMax: Float64Array;
  // Per-feature random threshold candidates, drawn before partitioning.
  thresholds: Float64Array;
}

/** A node stored as parallel typed arrays (SoA — struct of arrays). */
export class ExtraTree {
  // Grown lazily; capacity doubled when exhausted.
  private feature!: Int32Array; // split feature index, or -1 for leaf
  private threshold!: Float64Array; // split threshold
  private left!: Int32Array; // left child node id
  private right!: Int32Array; // right child node id
  private value!: Float64Array; // leaf prediction (mean y at this leaf)
  private leaf!: Uint8Array; // 1 if leaf, 0 if internal
  private nNodes = 0;
  private capacity = 0;

  readonly nFeatures: number;
  readonly minSamplesLeaf: number;
  // max_features for regression defaults to n_features (1.0) — all features
  // considered at every split, the "extra" randomness coming entirely from
  // the random threshold draw. Caller can override.
  readonly maxFeatures: number;

  constructor(nFeatures: number, minSamplesLeaf = 10, maxFeatures?: number) {
    this.nFeatures = nFeatures;
    this.minSamplesLeaf = minSamplesLeaf;
    this.maxFeatures = maxFeatures ?? nFeatures;
  }

  /**
   * Fit the tree on X (n×d, row-major Float64Array) and y (length n).
   * The rng is the per-tree PRNG — each tree gets its own seed so the
   * ensemble's trees differ.
   */
  fit(X: Float64Array, y: Float64Array, rng: () => number): void {
    const n = y.length;
    this.capacity = Math.max(16, Math.min(1 << 20, Math.ceil((2 * n) / this.minSamplesLeaf) * 2));
    this.feature = new Int32Array(this.capacity).fill(-1);
    this.threshold = new Float64Array(this.capacity);
    this.left = new Int32Array(this.capacity).fill(-1);
    this.right = new Int32Array(this.capacity).fill(-1);
    this.value = new Float64Array(this.capacity);
    this.leaf = new Uint8Array(this.capacity);
    this.nNodes = 0;

    const work: WorkBuffers = {
      leftIdx: new Int32Array(n),
      rightIdx: new Int32Array(n),
      featMin: new Float64Array(this.nFeatures),
      featMax: new Float64Array(this.nFeatures),
      thresholds: new Float64Array(this.nFeatures),
    };

    // Root: all sample indices.
    const rootSamples = new Int32Array(n);
    for (let i = 0; i < n; i++) rootSamples[i] = i;

    // Iterative depth-first build using an explicit stack of (nodeId, samples,
    // samplesLen) frames. (Recursion would blow the JS stack on deep trees.)
    type Frame = { nodeId: number; samples: Int32Array; len: number };
    const stack: Frame[] = [];
    const rootId = this.allocNode();
    stack.push({ nodeId: rootId, samples: rootSamples, len: n });

    while (stack.length > 0) {
      const frame = stack.pop()!;
      const { nodeId, samples, len } = frame;
      // Compute node value (mean of y) regardless of leaf status.
      let sum = 0;
      for (let i = 0; i < len; i++) sum += y[samples[i]];
      const mean = sum / len;
      this.value[nodeId] = mean;

      // Stop conditions (sklearn _tree.pyx:256):
      //   n < 2*min_samples_leaf  OR  impurity ~ 0 (pure node).
      if (len < 2 * this.minSamplesLeaf) {
        this.leaf[nodeId] = 1;
        continue;
      }
      // Pure node (all y identical) — impurity ~ 0.
      let allSame = true;
      for (let i = 1; i < len; i++) {
        if (y[samples[i]] !== y[samples[0]]) {
          allSame = false;
          break;
        }
      }
      if (allSame) {
        this.leaf[nodeId] = 1;
        continue;
      }

      // Find the best random split.
      const split = this.findBestSplit(X, y, samples, len, rng, work);
      if (split === null) {
        this.leaf[nodeId] = 1;
        continue;
      }

      // Partition samples into left/right by the chosen feature+threshold.
      const { feature: f, threshold: t } = split;
      let nLeft = 0;
      let nRight = 0;
      for (let i = 0; i < len; i++) {
        const s = samples[i];
        if (X[s * this.nFeatures + f] <= t) {
          work.leftIdx[nLeft++] = s;
        } else {
          work.rightIdx[nRight++] = s;
        }
      }

      // Alloc children, store the split, push frames.
      const leftId = this.allocNode();
      const rightId = this.allocNode();
      this.feature[nodeId] = f;
      this.threshold[nodeId] = t;
      this.left[nodeId] = leftId;
      this.right[nodeId] = rightId;
      this.leaf[nodeId] = 0;
      // Copy out of the work buffer into fresh arrays (the work buffer is
      // reused for the next node).
      const leftSamples = new Int32Array(nLeft);
      for (let i = 0; i < nLeft; i++) leftSamples[i] = work.leftIdx[i];
      const rightSamples = new Int32Array(nRight);
      for (let i = 0; i < nRight; i++) rightSamples[i] = work.rightIdx[i];
      // Push right first so left is processed first (DFS order).
      stack.push({ nodeId: rightId, samples: rightSamples, len: nRight });
      stack.push({ nodeId: leftId, samples: leftSamples, len: nLeft });
    }
  }

  private allocNode(): number {
    if (this.nNodes >= this.capacity) {
      // Double capacity, copy all arrays.
      const newCap = this.capacity * 2;
      const f = new Int32Array(newCap).fill(-1);
      f.set(this.feature);
      const t = new Float64Array(newCap);
      t.set(this.threshold);
      const l = new Int32Array(newCap).fill(-1);
      l.set(this.left);
      const r = new Int32Array(newCap).fill(-1);
      r.set(this.right);
      const v = new Float64Array(newCap);
      v.set(this.value);
      const lf = new Uint8Array(newCap);
      lf.set(this.leaf);
      this.feature = f;
      this.threshold = t;
      this.left = l;
      this.right = r;
      this.value = v;
      this.leaf = lf;
      this.capacity = newCap;
    }
    const id = this.nNodes++;
    return id;
  }

  /**
   * Find the best random split for the node's samples. Returns null if no
   * valid split survives the min_samples_leaf check.
   *
   * One sample-outer pass computes per-feature min/max. Then for each
   * non-constant feature we draw a random threshold and evaluate it.
   */
  private findBestSplit(
    X: Float64Array,
    y: Float64Array,
    samples: Int32Array,
    len: number,
    rng: () => number,
    work: WorkBuffers,
  ): { feature: number; threshold: number } | null {
    const d = this.nFeatures;
    const { featMin, featMax } = work;
    // Initialise min/max from the first sample.
    for (let f = 0; f < d; f++) {
      featMin[f] = Infinity;
      featMax[f] = -Infinity;
    }
    for (let i = 0; i < len; i++) {
      const s = samples[i];
      const base = s * d;
      for (let f = 0; f < d; f++) {
        const v = X[base + f];
        if (v < featMin[f]) featMin[f] = v;
        if (v > featMax[f]) featMax[f] = v;
      }
    }

    // Iterate features in random order (Fisher-Yates partial shuffle for
    // max_features). When max_features == d (our default), iterate all.
    let bestProxy = -Infinity;
    let bestFeature = -1;
    let bestThreshold = 0;

    // Candidate features (in random order).
    const featOrder = this.featureOrder(d, rng);
    const featsToEval = Math.min(this.maxFeatures, d);

    for (let order = 0; order < featsToEval; order++) {
      const f = featOrder[order];
      const lo = featMin[f];
      const hi = featMax[f];
      if (hi - lo <= FEATURE_THRESHOLD) continue; // constant feature, skip
      // Draw threshold uniformly in [lo, hi).
      let t = lo + rng() * (hi - lo);
      if (t >= hi) t = lo; // guard against the tiny float edge case
      // Partition and score: maximise sum_left²/n_left + sum_right²/n_right.
      let sumLeft = 0;
      let sumRight = 0;
      let nLeft = 0;
      let nRight = 0;
      for (let i = 0; i < len; i++) {
        const s = samples[i];
        const xv = X[s * d + f];
        const yv = y[s];
        if (xv <= t) {
          sumLeft += yv;
          nLeft++;
        } else {
          sumRight += yv;
          nRight++;
        }
      }
      // Reject splits that violate min_samples_leaf on either child.
      if (nLeft < this.minSamplesLeaf || nRight < this.minSamplesLeaf) continue;
      const proxy = (sumLeft * sumLeft) / nLeft + (sumRight * sumRight) / nRight;
      // Strict > : on ties the first-drawn feature wins (matches sklearn).
      if (proxy > bestProxy) {
        bestProxy = proxy;
        bestFeature = f;
        bestThreshold = t;
      }
    }

    if (bestFeature < 0) return null;
    return { feature: bestFeature, threshold: bestThreshold };
  }

  /** Random feature order via Fisher-Yates on a reusable permutation. */
  private featureOrder(d: number, rng: () => number): Int32Array {
    // Lazily allocate the permutation buffer (one per tree).
    if (this._perm === null || this._perm.length !== d) {
      this._perm = new Int32Array(d);
      for (let i = 0; i < d; i++) this._perm[i] = i;
    }
    // Fisher-Yates (full shuffle — cheap relative to the partition pass).
    for (let i = d - 1; i > 0; i--) {
      const j = Math.floor(rng() * (i + 1));
      const tmp = this._perm[i];
      this._perm[i] = this._perm[j];
      this._perm[j] = tmp;
    }
    return this._perm;
  }
  private _perm: Int32Array | null = null;

  /** Predict y for a single d-dim feature vector x. Routes down the tree. */
  predict(x: Float64Array): number {
    let node = 0;
    while (this.leaf[node] === 0) {
      const f = this.feature[node];
      const t = this.threshold[node];
      node = x[f] <= t ? this.left[node] : this.right[node];
    }
    return this.value[node];
  }

  /**
   * Predict for a single row inside a flat row-major buffer, starting at
   * `rowOffset`. Same routing as `predict`, but avoids allocating a per-row
   * subarray — used by the forest's per-action predict loop, which runs
   * N_ACTIONS × nTrees times per agent step.
   */
  predictRowOffset(X: Float64Array, rowOffset: number): number {
    let node = 0;
    while (this.leaf[node] === 0) {
      const f = this.feature[node];
      const t = this.threshold[node];
      node = X[rowOffset + f] <= t ? this.left[node] : this.right[node];
    }
    return this.value[node];
  }

  /** Number of nodes (for diagnostics / sizing). */
  get size(): number {
    return this.nNodes;
  }

  /**
   * Serialise to a plain bag of typed arrays (for postMessage to the main
   * thread after a worker-side fit). The nFeatures / minSamplesLeaf travel
   * alongside; the constructor params are not needed to rehydrate.
   */
  serialize(): TreeBuffers {
    // Slice down to actual size so we don't transfer dead capacity.
    return {
      feature: this.feature.slice(0, this.nNodes),
      threshold: this.threshold.slice(0, this.nNodes),
      left: this.left.slice(0, this.nNodes),
      right: this.right.slice(0, this.nNodes),
      value: this.value.slice(0, this.nNodes),
      leaf: this.leaf.slice(0, this.nNodes),
      nFeatures: this.nFeatures,
    };
  }

  /** Rehydrate a tree from serialized buffers (worker → main thread). */
  static fromSerialized(bufs: TreeBuffers): ExtraTree {
    const t = new ExtraTree(bufs.nFeatures);
    t.feature = bufs.feature;
    t.threshold = bufs.threshold;
    t.left = bufs.left;
    t.right = bufs.right;
    t.value = bufs.value;
    t.leaf = bufs.leaf;
    t.nNodes = bufs.feature.length;
    t.capacity = bufs.feature.length;
    return t;
  }
}

/** Serializable bag of typed arrays representing one trained tree. */
export interface TreeBuffers {
  feature: Int32Array;
  threshold: Float64Array;
  left: Int32Array;
  right: Int32Array;
  value: Float64Array;
  leaf: Uint8Array;
  nFeatures: number;
}
